import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';

const auth = 'auth';
const user = 'user';

const apiRoutes = {
	register: auth + '/register',
	login: auth + '/login',
	verifyTwoFA: auth + '/verify-2fa',
	resendVerifyTwoFA: auth + '/resend-verify-2fa',
	newPassword: auth + '/new-password',
	resetPassword: auth + '/reset-password',
	checkEmailExists: auth + '/check-email-exist',
	myUser: user + '/my-user',
}

@Injectable({ providedIn: 'root' })
export class AuthService {
	private headers!: HttpHeaders;
	private userSubject = new BehaviorSubject<any>(null);
	private clientSubject = new BehaviorSubject<any>(null);
	private researchUserSubject = new BehaviorSubject<any>(null);
	private lockscrn$ = new BehaviorSubject<boolean>(false);
	timeOutObj: any;
	token: any = null;
	clientID: any = '';

	constructor(private http: HttpClient, private tokenService: TokenService, private toastr: ToastrService, public jwtHelper: JwtHelperService, private router: Router) { 
		this.token = localStorage.getItem('token');
		// this.clientID = localStorage.getItem('client_id') ? localStorage.getItem('client_id') : '';
		// this.headers = new HttpHeaders({ 'Content-Type': 'application/json', 'client_id': this.clientID });
	}

	fetchUserDetails() {
		this.getloggedInUserDetails().subscribe({
			next: (user: any) => {
				this.userSubject.next(user);
				this.router.navigate(['/']);
			},
			error: () => {
				this.logout();
				this.toastErrorMessage('Somthing went wrong!', 'Fetching User Error')
			}
		});
	}

	register(data: any) {
		return this.http.post(environment.api + apiRoutes.register, data, { headers: this.headers });
	}

	checkEmailExists(data: any) {
		return this.http.post(environment.api + apiRoutes.checkEmailExists, data, { headers: this.headers });
	}

	login(email: string, password: string, remember_me: boolean) {
		return this.http.post(environment.api + apiRoutes.login, { email, password, remember_me }, { headers: this.headers });
	}

	getloggedInUserDetails() {
		this.clientID = localStorage.getItem('client_id') ? localStorage.getItem('client_id') : '';
		this.headers = new HttpHeaders({ 'Content-Type': 'application/json', 'client_id': this.clientID });
    return this.http.get<any>(environment.api + apiRoutes.myUser, { headers: this.headers });
  }

	verifyTwoFA(code: string) {
		return this.http.post<any>(environment.api + apiRoutes.verifyTwoFA, { code }, { headers: this.headers });
	}

	updateUserPassword(token: string, data: any) {
		const h = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'client_id': this.clientID });
		return this.http.post<any>(environment.api + apiRoutes.newPassword, data, { headers: h });
	}

	changeUserPassword(data: any) {
		return this.http.post<any>(environment.api + apiRoutes.newPassword, data, { headers: this.headers });
	}

	sendResetPassword(data: any) {
		return this.http.post<any>(environment.api + apiRoutes.resetPassword, data, { headers: this.headers });
	}

	resendVerifyTwoFA() {
		return this.http.get<any>(environment.api + apiRoutes.resendVerifyTwoFA, { headers: this.headers });
	}

	logout() {
		localStorage.removeItem('token');
		localStorage.removeItem('client_id');
		this.token = null;
		this.clientID = null;
		this.userSubject.next(null);
		this.clientSubject.next(null);
		this.researchUserSubject.next(null);
		this.router.navigate(['auth/login']);
	}

	setToken(token: any) {
		this.token = token;
		localStorage.setItem('token', this.token);
		this.fetchUserDetails();
	}

	public startTimer() {
		const d = new Date(0);
		const curr = Date.now();
		const diff = d.setUTCSeconds(Number.parseInt(this.tokenService.getTokenExp(this.token))) - curr;
		this.timeOutObj = setTimeout(() => {
			this.toggleLockScrn(true);
		}, diff);
	}

	get lockscreen$() {
		return this.lockscrn$.asObservable();
	}

	toggleLockScrn(lock: boolean) {
		this.lockscrn$.next(lock);
	}

	toastErrorMessage(detail: string | undefined, title: string | undefined) {
    this.toastr.error(detail, title);
  }

  toastSuccessMessage(detail: string | undefined, title: string | undefined) {
    this.toastr.success(detail, title);
  }

	setCurrentUserSubject(user: any) {
		this.userSubject.next(user);
	}

	get currentUserValue(): any {
		return this.userSubject.value;
	}

	setCurrentClientSubject(client: any) {
		this.researchUserSubject.next(null);
		this.setClientID(client.client_id);
		this.clientSubject.next(client);
	}

	setClientID(client_id: any) {
		this.clientID = client_id !== '' ? client_id.toString() : client_id;
		this.headers = this.headers.set('client_id', this.clientID);
		localStorage.setItem('client_id', this.clientID);
	}

	emptyClientSubject() {
		this.setClientID('');
		this.clientSubject.next(null);
		this.router.navigate(['/auth/clients']);
	}

	get currentClientValue(): any {
		return this.clientSubject.value;
	}

	setCurrentResearchUser(researchUser: any) {
		this.clientSubject.next(null);
		this.researchUserSubject.next(researchUser);
	}

	get currentResearchUserValue(): any {
		return this.researchUserSubject.value;
	}

	public get role(): string {
		const researchUser = this.researchUserSubject.value;
		if (researchUser) {
			const RESEARCH_PRIORITY = ['ADMIN', 'LEAD', 'ANALYST', 'VIEWER'];
			const roles: string[] = Array.isArray(researchUser.roles) ? researchUser.roles : [researchUser.role];
			return RESEARCH_PRIORITY.find(r => roles.includes(r)) ?? roles[0] ?? 'No Role';
		}
		if (this.clientSubject.value) {
			if (this.clientSubject.value.roles.length > 1) {
				if (this.clientSubject.value.roles.includes('ADMIN')) {
					return 'ADMIN';
				} else {
					if (this.clientSubject.value.roles.includes('OWNER')) {
						return 'OWNER';
					} else {
						return this.clientSubject.value.roles[0];
					}
				}
			} else {
				return this.clientSubject.value.roles[0];
			}
		} else {
			return 'No Role';
		}
	}
}